import {
  Platform,
  StyleProp,
  TextInput,
  TextInputProps,
  TextStyle,
  ViewStyle,
} from "react-native";

import React, { useMemo } from "react";
import { BoxProps } from "./AHTheme/BoxProps";
import { useScaleFactor } from "./AHScaleFactor";
import { useBoxStyle } from "./AHTheme/useBoxStyle";
import { ColorType, useTheme } from "./AHTheme/AHTheme";
import { useLayoutContext } from "./LayoutProvider";
import { resolveAHTextStyle } from "./Text/AHText";

type PickedTextInputProps = Pick<
  TextInputProps,
  | "autoCapitalize"
  | "autoCorrect"
  | "autoFocus"
  | "autoComplete"
  | "editable"
  | "multiline"
  | "returnKeyType"
  | "value"
  | "onChangeText"
  | "onBlur"
  | "onFocus"
  | "placeholder"
  | "maxLength"
  | "keyboardType"
  | "secureTextEntry"
  | "textContentType"
  | "returnKeyLabel"
  | "onSubmitEditing"
  | "style"
  | "onEndEditing"
  | "submitBehavior"
  | "numberOfLines"
>;
export type InputProps = {
  tabIndex?: number;
  disabled?: boolean;
  inputComponent?: React.ComponentType<TextInputProps>;
  placeholderColor?: ColorType;
  ref?: React.RefObject<TextInput | null>;
} & PickedTextInputProps &
  BoxProps;

export const Input = ({
  tabIndex,
  disabled,
  inputComponent,
  placeholderColor,
  ref,
  ...props
}: InputProps) => {
  const sf = useScaleFactor();
  const boxableStyle = useBoxStyle({
    ...props,
    space: props.space ?? "medium",
    cornerRadius: props.cornerRadius ?? "large",
    color: props.color ?? "surface-primary",
    borderColor: props.borderColor ?? "border-primary",
  });
  const theme = useTheme();
  const layout = useLayoutContext();

  const webProps =
    Platform.OS === "web"
      ? { disabled: disabled === true ? true : undefined }
      : {};
  const placeholderTextColor = useTheme()[placeholderColor ?? "text-washed"];
  const style = useMemo(
    () => [
      {
        borderWidth: 1,
        fontWeight: "normal", // fixes android custom fonts :S
      },
      boxableStyle,
      resolveAHTextStyle("list-item-title", sf, theme, layout),
      props.style,
    ],
    [boxableStyle, sf, theme, layout, props.style]
  );
  const InputComponent = inputComponent ?? TextInput;

  return (
    <InputComponent
      {...({ tabIndex } as any)}
      {...webProps}
      ref={ref}
      scrollEnabled={false}
      underlineColorAndroid="transparent"
      autoCorrect={false}
      autoCapitalize="none"
      returnKeyType={props.multiline ? "default" : "done"}
      submitBehavior="submit"
      blurOnSubmit={!props.multiline}
      {...props}
      style={style}
      placeholderTextColor={placeholderTextColor}
    />
  );
};
